import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  CommentsPostDisplayPaginationAllInOneComponent,
  ConversationApiService,
  ConversationCommentListQuery,
  ConversationDefinition,
  ConversationStateService,
  InsightMarkdownPipe,
  PaginatedComments,
} from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { finalize, map } from 'rxjs/operators';
import { AuthService } from '../../auth';
import { LanguageService, reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { BreadcrumbService } from '../../navigation/breadcrumb/breadcrumb.service';
import { PageTitleService } from '../../navigation/title/title.service';
import { WindowService } from '../../utilities';
import { ShowBannerComponent } from '../show-banner/show-banner.component';
import { ShowPlayerComponent } from '../show-player/show-player.component';
import { ShowsCreditsComponent } from '../shows-credits/shows-credits.component';
import { ShowsLikeButtonComponent } from '../shows-like-button/shows-like-button.component';
import { ShowsSubscribeButtonComponent } from '../shows-subscribe-button/shows-subscribe-button.component';
import { CHANNEL_NAMES, chapterFromParam, chapterStart, cutForChapter, defaultCut, formatTimecode, otherLinks, playerLink } from '../shows-player';
import { ShowsTranslationFollower } from '../shows-translation';
import { Chapter, Cut, DEFAULT_SHOWS_DEFINITION, clipPath, Episode, Show, ShowsDefinition, ShowsItemRef, Tool } from '../shows.model';
import { ShowsService } from '../shows.service';

const COMMENTS_PER_PAGE = 10;
const MORE_EPISODES_SHOWN = 5;

/**
 * One episode: `/shows/{show}/{episode}`, and `?chapter={position}` to open at a chapter.
 *
 * ## Playing
 *
 * The reader starts on a video cut in their language where there is one (`defaultCut()`), and
 * may switch between cuts. A chapter opens on the playing cut if it was timed there, else on a
 * cut where it was; the player is reloaded at the chapter's time — `shows-player.ts`. A chapter
 * link names the chapter, so it works whichever cut the reader ends up on.
 *
 * ## Credits and tools
 *
 * The credits are the show's crew and the episode's. A linked tool says who recommends it when
 * that person is in the credits: a recommendation belongs to the tool, not to the episode.
 *
 * ## Comments, likes and language
 *
 * Comments are the episode's conversation in the reader's language, created by its first
 * comment, as on tips; posting needs the Conversationalist role. Likes are private. The text is
 * asked for in the reader's language as on the news pages.
 */
@Component({
  selector: 'multisite-episode-page',
  templateUrl: './episode-page.component.html',
  imports: [
    DatePipe,
    RouterModule,
    TranslocoModule,
    InsightMarkdownPipe,
    LoaderGlobeAnimationDetailComponent,
    CommentsPostDisplayPaginationAllInOneComponent,
    ShowBannerComponent,
    ShowPlayerComponent,
    ShowsCreditsComponent,
    ShowsLikeButtonComponent,
    ShowsSubscribeButtonComponent,
  ],
  providers: [provideTranslocoScope('shows', 'tools', 'ideas')],
})
export class EpisodePageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private showsService = inject(ShowsService);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);
  private conversationApi = inject(ConversationApiService);
  private conversationState = inject(ConversationStateService);
  private breadcrumbService = inject(BreadcrumbService);
  private pageTitleService = inject(PageTitleService);
  private windowService = inject(WindowService);

  readonly definition: ShowsDefinition = this.route.snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;
  readonly clipPath = clipPath;
  readonly channelNames = CHANNEL_NAMES;
  readonly timecode = formatTimecode;
  readonly translation = new ShowsTranslationFollower<Episode>();
  readonly user = toSignal(this.authService.user);

  episode = signal<Episode | null>(null);
  show = signal<Show | null>(null);
  moreEpisodes = signal<Episode[]>([]);
  loading = signal(false);
  notFound = signal(false);
  failed = signal(false);

  /** The cut playing, by id. */
  cutId = signal<string | null>(null);
  /** Where the player opens, in seconds; and whether it plays at once. */
  startAt = signal<number | null>(null);
  autoplay = signal(false);
  /** Changing it rebuilds the player, so choosing the same chapter twice still reopens it. */
  playerKey = signal(0);
  openChapterPosition = signal<number | null>(null);

  paginatedComments = signal<PaginatedComments | undefined>(undefined);
  loadingComments = signal(false);
  clearCommentForm = signal(false);
  conversationId = signal<number | null>(null);
  conversationName = signal<string | undefined>(undefined);

  readonly ref = computed<ShowsItemRef>(() => ({ kind: 'episode', show: this.showSlug(), slug: this.episodeSlug() }));

  readonly cut = computed<Cut | null>(() => this.episode()?.cuts.find((cut) => cut.id === this.cutId()) ?? null);
  readonly player = computed(() => {
    const cut = this.cut();

    return cut ? playerLink(cut.channelLinks) : null;
  });
  readonly alsoOn = computed(() => {
    const cut = this.cut();

    return cut ? otherLinks(cut.channelLinks) : [];
  });

  /** Whether the reader may post a comment: the backend's own rule, shown before they try. */
  readonly mayComment = computed(() => Boolean(this.user()) && this.authService.checkRole('Conversationalist'));

  /** Who took part: the show's regular crew, then the episode's own. */
  readonly credits = computed(() => [...(this.show()?.crew ?? []), ...(this.episode()?.crew ?? [])]);

  readonly showSlug = signal('');
  readonly episodeSlug = signal('');

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.showSlug.set(params.get('show') ?? '');
      this.episodeSlug.set(params.get('episode') ?? '');
      this.windowService.goToTop();
      this.load();
    });

    this.route.queryParamMap.pipe(map((params) => params.get('chapter'))).subscribe((value) => {
      const episode = this.episode();
      const chapter = episode ? chapterFromParam(value, episode.chapters) : null;

      if (chapter && chapter.position !== this.openChapterPosition()) {
        this.open(chapter);
      }
    });

    reloadOnLocaleChange(() => {
      this.conversationId.set(null);
      this.load();
    });
  }

  chooseCut(cut: Cut): void {
    this.cutId.set(cut.id);
    this.startAt.set(null);
    this.autoplay.set(false);
    this.openChapterPosition.set(null);
  }

  cutLabel(cut: Cut, cuts: Cut[]): string {
    const languages = new Set(cuts.map((c) => c.language));

    return languages.size > 1 && cut.language ? cut.language.toUpperCase() : '';
  }

  /** A chapter chosen on the page: open it, and put it in the address so the link can be shared. */
  chooseChapter(chapter: Chapter): void {
    this.open(chapter);
    this.router.navigate([], { relativeTo: this.route, queryParams: { chapter: chapter.position }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  /** The cut a chapter would play on, or null if no cut can open at it. */
  chapterCut(chapter: Chapter): Cut | null {
    return cutForChapter(chapter, this.cut(), this.episode()?.cuts ?? []);
  }

  chapterTime(chapter: Chapter): number | null {
    return chapterStart(chapter, this.chapterCut(chapter)?.id ?? this.cutId());
  }

  /** "Recommended by" names a recommender who is in this episode's credits. */
  recommenders(tool: Tool): string[] {
    const names = new Set(this.credits().map((member) => member.name));

    return tool.recommendedBy.filter((name) => names.has(name));
  }

  getComments(page: number): void {
    const conversationId = this.conversationId();

    if (conversationId) {
      this.loadComments(conversationId, page);
      return;
    }

    this.previewConversation(page);
  }

  postComment(message: string): void {
    const conversationId = this.conversationId();
    const page = this.paginatedComments()?.meta.current_page ?? 1;

    if (conversationId) {
      this.conversationApi.createCommentInConversation(conversationId, message).subscribe({
        next: () => {
          this.clearCommentForm.update((value) => !value);
          this.getComments(page);
        },
      });
      return;
    }

    this.conversationState.submitFirstCommentByDefinition(this.conversationDefinition(), message, this.commentQuery(page)).subscribe({
      next: (result) => {
        this.conversationId.set(result.conversationId);
        this.paginatedComments.set(result.comments);
        this.clearCommentForm.update((value) => !value);
      },
    });
  }

  private open(chapter: Chapter): void {
    const cut = this.chapterCut(chapter);

    if (!cut) {
      return;
    }

    this.cutId.set(cut.id);
    this.startAt.set(chapterStart(chapter, cut.id));
    this.autoplay.set(true);
    this.openChapterPosition.set(chapter.position);
    this.playerKey.update((key) => key + 1);
  }

  private load(): void {
    this.translation.stop();
    this.loading.set(true);
    this.notFound.set(false);
    this.failed.set(false);
    this.episode.set(null);

    this.showsService
      .getEpisode(this.showSlug(), this.episodeSlug())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (episode) => {
          this.present(episode);
          this.startPlaying(episode);
          this.getComments(1);
          this.translation.follow(this.ref(), episode, (translated) => this.present(this.keepRelations(translated)));
        },
        error: (error) => (error?.status === 404 ? this.notFound.set(true) : this.failed.set(true)),
      });

    this.showsService.getShow(this.showSlug()).subscribe({
      next: (show) => {
        this.show.set(show);
        this.breadcrumbService.setBreadcrumbFragment({ urlFragment: 'show', fragmentName: show.name });
      },
      error: () => this.show.set(null),
    });

    this.showsService.getEpisodes(this.showSlug(), { pageSize: MORE_EPISODES_SHOWN + 1 }).subscribe({
      next: (page) => this.moreEpisodes.set(page.items.filter((other) => other.slug !== this.episodeSlug()).slice(0, MORE_EPISODES_SHOWN)),
      error: () => this.moreEpisodes.set([]),
    });
  }

  private present(episode: Episode): void {
    this.episode.set(episode);
    this.pageTitleService.setTitle(episode.title);
  }

  /** On arrival: the reader's cut, or the chapter the address names. */
  private startPlaying(episode: Episode): void {
    const reader = this.languageService.activeLanguage()?.languageKey ?? null;

    this.cutId.set(defaultCut(episode.cuts, reader, episode.language)?.id ?? null);
    this.startAt.set(null);
    this.autoplay.set(false);
    this.openChapterPosition.set(null);

    const chapter = chapterFromParam(this.route.snapshot.queryParamMap.get('chapter'), episode.chapters);

    if (chapter) {
      this.open(chapter);
    }
  }

  /** A translation's answer carries no related records; keep the ones the page has. */
  private keepRelations(answer: Episode): Episode {
    const current = this.episode();

    if (!current || answer.cuts.length) {
      return answer;
    }

    const { cuts, chapters, crew, tools, clips, links, quotations, images, cultures, topics, tags } = current;

    return { ...answer, cuts, chapters, crew, tools, clips, links, quotations, images, cultures, topics, tags };
  }

  private previewConversation(page: number): void {
    this.loadingComments.set(true);

    this.conversationApi.previewConversation(this.conversationDefinition()).subscribe({
      next: (preview) => {
        const id = (preview as { id?: unknown })?.id;
        this.conversationName.set((preview as { name?: string })?.name);

        if (typeof id === 'number') {
          this.conversationId.set(id);
          this.loadComments(id, page);
        } else {
          this.paginatedComments.set(undefined);
          this.loadingComments.set(false);
        }
      },
      error: () => this.loadingComments.set(false),
    });
  }

  private loadComments(conversationId: number, page: number): void {
    this.loadingComments.set(true);

    this.conversationApi
      .listCommentsByConversationId(conversationId, this.commentQuery(page))
      .pipe(finalize(() => this.loadingComments.set(false)))
      .subscribe({ next: (comments) => this.paginatedComments.set(comments) });
  }

  /** The episode's conversation in the reader's language — the `episode_single_untyped` template. */
  private conversationDefinition(): ConversationDefinition {
    const id = Number(this.episode()?.id);

    return {
      locale: this.languageService.activeLanguage()?.languageKey || 'en',
      category: null,
      type: null,
      episodes: Number.isFinite(id) ? [id] : [],
    };
  }

  private commentQuery(page: number): ConversationCommentListQuery {
    return { page, per_page: COMMENTS_PER_PAGE, sort_by: 'created_at', sort_direction: 'desc' };
  }
}
